import type {
  DistributionSeries,
  LatencyDistributionSpec,
  ResolvedFigure,
  ShapeRole,
} from '../model.js';
import {
  box,
  finishFigure,
  point,
  roundGeometry,
  SceneBuilder,
  validateViewport,
} from '../scene-builder.js';

interface ResolvedSeries {
  source: DistributionSeries;
  sorted: number[];
  bins: number[];
  percentiles: Map<number, number>;
}

interface MarkerLabel {
  semanticId: string;
  seriesIndex: number;
  percentile: 50 | 95 | 99;
  value: number;
  x: number;
  labelX: number;
  row: number;
}

export function latencyBinId(seriesId: string, binIndex: number): string {
  return `plot.bin.${seriesId}.${binIndex}`;
}

export function latencyPercentileId(seriesId: string, percentile: 50 | 95 | 99): string {
  return `plot.marker.${seriesId}.p${percentile}`;
}

/**
 * Compiles raw latency samples into a deterministic histogram. The author
 * supplies observations and semantic focus only; binning, scales, geometry,
 * tail treatment, percentile placement, and responsive label policy belong
 * to this plot-view compiler.
 */
export function compileLatencyDistribution(spec: LatencyDistributionSpec): ResolvedFigure {
  validateLatencyDistribution(spec);

  const builder = new SceneBuilder();
  const { width, height } = spec.viewport;
  const narrow = width <= 390;
  const marginLeft = narrow ? 48 : 62;
  const marginRight = narrow ? 14 : 24;
  // Percentile labels live in a dedicated lane above the data marks. Keeping
  // them out of the histogram means dense p50 pairs never fight the tallest
  // bars, and the same rule holds for one- and two-series plots.
  const plotTop = spec.series.length === 2
    ? (narrow ? 100 : 96)
    : (narrow ? 88 : 84);
  const plotBottom = height - (spec.series.length === 2 ? 88 : 72);
  const plotWidth = width - marginLeft - marginRight;
  const plotHeight = plotBottom - plotTop;
  const binCount = width <= 340 ? 8 : width <= 450 ? 10 : 12;
  const maxSample = Math.max(...spec.series.flatMap((series) => series.samples));
  const domainMax = niceCeil(maxSample);
  const binWidthValue = domainMax / binCount;
  const resolved = spec.series.map((source) => resolveSeries(source, spec.percentileMarkers, binCount, binWidthValue));
  const maxBinCount = Math.max(1, ...resolved.flatMap((series) => series.bins));
  const yMax = niceCeil(maxBinCount);
  const groupWidth = plotWidth / binCount;
  const warnings: string[] = [];

  builder.text('plot.eyebrow', 'plot', 'annotation', point(marginLeft, 25), 'LATENCY DISTRIBUTION', {
    size: 10,
    mono: true,
    weight: 700,
    uppercase: true,
  });
  builder.text('plot.summary', 'plot', 'structure', point(marginLeft, 46), summary(spec, resolved, narrow), {
    size: narrow ? 10 : 11,
    mono: true,
    weight: 700,
  });

  const primaryP95 = resolved[0]!.percentiles.get(95) ?? percentile(resolved[0]!.sorted, 95);
  const tailX = scaleX(primaryP95, domainMax, marginLeft, plotWidth);
  builder.rect('plot.tail.region', 'plot.tail', 'tail', box(tailX, plotTop, marginLeft + plotWidth - tailX, plotHeight), {
    label: `Tail latency begins at ${formatValue(primaryP95)} ${spec.unit}`,
    interactive: true,
  });
  builder.text('plot.tail.label', 'plot.tail', 'annotation', point(Math.min(marginLeft + plotWidth - 4, tailX + 6), plotTop + 14), `TAIL ≥ ${formatValue(primaryP95)} ${spec.unit}`, {
    size: narrow ? 7.5 : 8.5,
    mono: true,
    weight: 700,
    align: tailX > marginLeft + plotWidth - 74 ? 'end' : 'start',
    uppercase: true,
  });
  builder.anchor('plot.tail.start', 'plot.tail', point(tailX, plotTop), 'down');
  const tailHitX = clamp(tailX - 8, marginLeft, marginLeft + plotWidth - 16);
  builder.hit('plot.tail', box(tailHitX, plotTop, marginLeft + plotWidth - tailHitX, plotHeight));

  for (let tick = 0; tick <= 4; tick += 1) {
    const fraction = tick / 4;
    const y = plotBottom - plotHeight * fraction;
    const count = Math.round(yMax * fraction);
    builder.line(`plot.grid.y.${tick}`, 'plot.grid', 'grid', point(marginLeft, y), point(marginLeft + plotWidth, y), {
      ...(tick > 0 ? { dash: '2 5' } : {}),
    });
    builder.text(`plot.axis.y.${tick}`, 'plot.axis.y', 'annotation', point(marginLeft - 8, y + 3), String(count), {
      size: narrow ? 8 : 9,
      mono: true,
      align: 'end',
    });
  }

  for (let tick = 0; tick <= 4; tick += 1) {
    const fraction = tick / 4;
    const x = marginLeft + plotWidth * fraction;
    const value = domainMax * fraction;
    builder.line(`plot.tick.x.${tick}`, 'plot.axis.x', 'axis', point(x, plotBottom), point(x, plotBottom + 5));
    builder.text(`plot.axis.x.${tick}`, 'plot.axis.x', 'annotation', point(x, plotBottom + 19), formatValue(value), {
      size: narrow ? 8 : 9,
      mono: true,
      align: 'middle',
    });
  }

  builder.line('plot.axis.y.line', 'plot.axis.y', 'axis', point(marginLeft, plotTop), point(marginLeft, plotBottom));
  builder.line('plot.axis.x.line', 'plot.axis.x', 'axis', point(marginLeft, plotBottom), point(marginLeft + plotWidth, plotBottom));
  builder.text('plot.axis.y.label', 'plot.axis.y', 'annotation', point(13, plotTop + plotHeight / 2), 'REQUESTS / BIN', {
    size: narrow ? 8 : 9,
    mono: true,
    weight: 700,
    align: 'middle',
    rotate: -90,
    uppercase: true,
  });
  builder.text('plot.axis.x.label', 'plot.axis.x', 'annotation', point(marginLeft + plotWidth, plotBottom + 34), `LATENCY (${spec.unit})`, {
    size: narrow ? 8 : 9,
    mono: true,
    weight: 700,
    align: 'end',
    uppercase: true,
  });

  resolved.forEach((series, seriesIndex) => {
    const barGap = narrow ? 1.5 : 2;
    const usableGroupWidth = groupWidth - barGap * 2;
    const barWidth = usableGroupWidth / resolved.length;

    series.bins.forEach((count, binIndex) => {
      const semanticId = latencyBinId(series.source.id, binIndex);
      const barX = marginLeft + binIndex * groupWidth + barGap + seriesIndex * barWidth;
      const rawHeight = (count / yMax) * plotHeight;
      const barHeight = count === 0 ? 1 : Math.max(2, rawHeight);
      const barY = plotBottom - barHeight;
      const lower = binIndex * binWidthValue;
      const upper = (binIndex + 1) * binWidthValue;
      const role: ShapeRole = lower >= primaryP95 ? 'tail' : 'series';
      const accessibleLabel = `${series.source.label}: ${count} requests from ${formatValue(lower)} to ${formatValue(upper)} ${spec.unit}`;

      builder.rect(`${semanticId}.bar`, semanticId, role, box(barX, barY, Math.max(1, barWidth - 1), barHeight), {
        radius: width >= 520 ? 2 : 1,
        label: accessibleLabel,
        interactive: true,
      });
      builder.anchor(`${semanticId}.top`, semanticId, point(barX + Math.max(1, barWidth - 1) / 2, barY), 'up');
      builder.hit(semanticId, box(barX, Math.max(plotTop, barY - 10), Math.max(3, barWidth - 1), Math.min(plotHeight, barHeight + 10)));
      builder.relation(`${semanticId}.samples`, 'bins', `plot.series.${series.source.id}`, semanticId, accessibleLabel);
    });
  });

  const markers = layoutMarkers(spec, resolved, domainMax, marginLeft, plotWidth, narrow);
  markers.forEach((marker) => {
    const role: ShapeRole = marker.percentile >= 95 ? 'tail' : 'dimension';
    builder.line(`${marker.semanticId}.line`, marker.semanticId, role, point(marker.x, plotTop), point(marker.x, plotBottom), {
      dash: marker.seriesIndex === 0 ? '5 4' : '2 4',
    });
    const labelY = plotTop - 8 - marker.row * 14;
    builder.line(`${marker.semanticId}.leader`, marker.semanticId, 'annotation', point(marker.x, plotTop), point(marker.labelX, labelY + 3));
    builder.text(`${marker.semanticId}.label`, marker.semanticId, role, point(marker.labelX, labelY), `p${marker.percentile} ${formatValue(marker.value)}`, {
      size: narrow ? 7.5 : 8.5,
      mono: true,
      weight: 700,
      align: 'middle',
    });
    builder.anchor(`${marker.semanticId}.axis`, marker.semanticId, point(marker.x, plotBottom), 'down');
    builder.hit(marker.semanticId, box(clamp(marker.x - 9, marginLeft, marginLeft + plotWidth - 18), plotTop, 18, plotHeight));
    builder.relation(`${marker.semanticId}.percentile`, 'percentile-of', `plot.series.${resolved[marker.seriesIndex]!.source.id}`, marker.semanticId, `p${marker.percentile} is ${formatValue(marker.value)} ${spec.unit}`);
  });

  const legendY = height - (spec.series.length === 2 ? 37 : 26);
  const legendWidth = plotWidth / spec.series.length;
  resolved.forEach((series, seriesIndex) => {
    const semanticId = `plot.series.${series.source.id}`;
    const x = marginLeft + legendWidth * seriesIndex;
    const label = compactLabel(series.source.label, narrow ? Math.max(10, Math.floor((legendWidth - 24) / 5.3)) : 34);
    builder.line(`${semanticId}.key`, semanticId, 'series', point(x, legendY - 3), point(x + 18, legendY - 3), {
      ...(seriesIndex === 1 ? { dash: '3 3' } : {}),
    });
    builder.text(`${semanticId}.label`, semanticId, 'structure', point(x + 24, legendY), label, {
      size: narrow ? 8 : 9,
      mono: true,
      weight: 700,
    });
    const legendBounds = box(x, legendY - 18, legendWidth, 30);
    builder.anchor(`${semanticId}.legend`, semanticId, point(x + 9, legendY - 3), 'right');
    builder.hit(semanticId, legendBounds);
    if (label !== series.source.label) warnings.push(`Series label compacted: ${series.source.id}`);
  });

  if (narrow) warnings.push('Plot uses the compact percentile-label policy at this width.');
  if (maxSample / Math.max(1, primaryP95) > 2) warnings.push('Extreme observations extend the domain beyond twice p95; inspect the tail rather than the mean.');

  const active = activeSemanticIds(spec, resolved, binWidthValue, primaryP95);
  active.push(...(spec.emphasis ?? []));

  return finishFigure(spec, builder, {
    title: 'Latency is a distribution, not one average',
    subtitle: spec.series.length === 2
      ? 'Compare the body and the tail: an optimization can improve one without fixing the other.'
      : 'The percentile markers expose the typical request and the costly tail separately.',
    state: `${spec.focus ?? 'overview'} · ${spec.series.length} series · ${spec.series.reduce((sum, series) => sum + series.samples.length, 0)} samples`,
    active,
    warnings,
  });
}

export function validateLatencyDistribution(spec: LatencyDistributionSpec): void {
  validateViewport(spec.viewport.width, spec.viewport.height);
  if (spec.unit !== 'ms') throw new Error('latency-distribution: only millisecond samples are supported');
  if (spec.series.length < 1 || spec.series.length > 2) throw new Error('latency-distribution: require one or two series');
  if (new Set(spec.percentileMarkers).size !== spec.percentileMarkers.length) throw new Error('latency-distribution: percentile markers must be unique');
  if (spec.percentileMarkers.length !== 3 || ![50, 95, 99].every((marker) => spec.percentileMarkers.includes(marker as 50 | 95 | 99))) {
    throw new Error('latency-distribution: p50, p95, and p99 markers are required');
  }
  if (!spec.percentileMarkers.every((marker) => marker === 50 || marker === 95 || marker === 99)) throw new Error('latency-distribution: unsupported percentile marker');
  if (!spec.provenance.trim()) throw new Error('latency-distribution: provenance is required');
  if (spec.focus === 'comparison' && spec.series.length !== 2) throw new Error('latency-distribution: comparison focus requires two series');

  const ids = spec.series.map((series) => series.id);
  if (new Set(ids).size !== ids.length) throw new Error('latency-distribution: series IDs must be unique');
  let totalSamples = 0;
  for (const series of spec.series) {
    if (!/^[a-z][a-z0-9-]*$/.test(series.id)) throw new Error(`latency-distribution: invalid series id ${series.id}`);
    if (!series.label.trim()) throw new Error(`latency-distribution: series ${series.id} requires a label`);
    if (series.samples.length < 8) throw new Error(`latency-distribution: series ${series.id} requires at least eight samples`);
    if (series.samples.length > 1_000) throw new Error(`latency-distribution: series ${series.id} exceeds the 1000-sample teaching limit`);
    if (series.samples.some((sample) => !Number.isFinite(sample) || sample < 0)) throw new Error(`latency-distribution: series ${series.id} contains an invalid sample`);
    totalSamples += series.samples.length;
  }
  if (totalSamples > 1_500) throw new Error('latency-distribution: total samples exceed the 1500-sample teaching limit');
  if (Math.max(...spec.series.flatMap((series) => series.samples)) <= 0) throw new Error('latency-distribution: at least one positive sample is required');
}

function resolveSeries(
  source: DistributionSeries,
  markers: Array<50 | 95 | 99>,
  binCount: number,
  binWidth: number,
): ResolvedSeries {
  const sorted = [...source.samples].sort((a, b) => a - b);
  const bins = Array.from({ length: binCount }, () => 0);
  for (const sample of sorted) {
    const index = Math.min(binCount - 1, Math.floor(sample / binWidth));
    bins[index] = bins[index]! + 1;
  }
  return {
    source,
    sorted,
    bins,
    percentiles: new Map(markers.map((marker) => [marker, percentile(sorted, marker)])),
  };
}

function percentile(sorted: number[], marker: number): number {
  const index = Math.max(0, Math.ceil((marker / 100) * sorted.length) - 1);
  return sorted[index]!;
}

function layoutMarkers(
  spec: LatencyDistributionSpec,
  resolved: ResolvedSeries[],
  domainMax: number,
  plotX: number,
  plotWidth: number,
  narrow: boolean,
): MarkerLabel[] {
  const markers = resolved.flatMap((series, seriesIndex) => spec.percentileMarkers.map((marker) => {
    const value = series.percentiles.get(marker)!;
    const x = scaleX(value, domainMax, plotX, plotWidth);
    return {
      semanticId: latencyPercentileId(series.source.id, marker),
      seriesIndex,
      percentile: marker,
      value,
      x,
      labelX: x,
      row: 0,
    } satisfies MarkerLabel;
  })).sort((a, b) => a.x - b.x || a.seriesIndex - b.seriesIndex || a.percentile - b.percentile);

  const labelWidth = narrow ? 43 : 50;
  const occupied: number[][] = [[], [], []];
  for (const marker of markers) {
    marker.labelX = clamp(marker.x, plotX + labelWidth / 2, plotX + plotWidth - labelWidth / 2);
    const row = occupied.findIndex((xs) => xs.every((x) => Math.abs(x - marker.labelX) >= labelWidth));
    marker.row = row >= 0 ? row : marker.seriesIndex % 3;
    occupied[marker.row]!.push(marker.labelX);
  }
  return markers;
}

function activeSemanticIds(
  spec: LatencyDistributionSpec,
  resolved: ResolvedSeries[],
  binWidth: number,
  primaryP95: number,
): string[] {
  if (spec.focus === 'body') {
    return resolved.flatMap((series) => series.bins.flatMap((_, index) => index * binWidth < primaryP95 ? [latencyBinId(series.source.id, index)] : []));
  }
  if (spec.focus === 'tail') {
    return [
      'plot.tail',
      ...resolved.flatMap((series) => [latencyPercentileId(series.source.id, 95), latencyPercentileId(series.source.id, 99)]),
      ...resolved.flatMap((series) => series.bins.flatMap((_, index) => index * binWidth >= primaryP95 ? [latencyBinId(series.source.id, index)] : [])),
    ];
  }
  if (spec.focus === 'comparison') return resolved.map((series) => `plot.series.${series.source.id}`);
  return ['plot'];
}

function summary(spec: LatencyDistributionSpec, resolved: ResolvedSeries[], narrow: boolean): string {
  const sampleCount = spec.series.reduce((sum, series) => sum + series.samples.length, 0);
  const p50 = resolved[0]!.percentiles.get(50) ?? percentile(resolved[0]!.sorted, 50);
  const p99 = resolved[0]!.percentiles.get(99) ?? percentile(resolved[0]!.sorted, 99);
  return narrow
    ? `n=${sampleCount} · p50 ${formatValue(p50)} · p99 ${formatValue(p99)} ${spec.unit}`
    : `${sampleCount} REQUESTS · PRIMARY p50 ${formatValue(p50)} ${spec.unit} · p99 ${formatValue(p99)} ${spec.unit}`;
}

function scaleX(value: number, max: number, x: number, width: number): number {
  return roundGeometry(x + (value / max) * width);
}

function niceCeil(value: number): number {
  if (value <= 0) return 1;
  const power = 10 ** Math.floor(Math.log10(value));
  const normalized = value / power;
  const nice = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return nice * power;
}

function formatValue(value: number): string {
  if (value >= 100 || Number.isInteger(value)) return String(Math.round(value));
  return String(Math.round(value * 10) / 10);
}

function compactLabel(value: string, max: number): string {
  if (value.length <= max) return value;
  if (max <= 3) return value.slice(0, max);
  return `${value.slice(0, Math.max(2, max - 2))}…${value.at(-1)}`;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
